// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import App from './App'
import router from './router'
import { store } from '@/store'
import './assets/css/style-dark.css'
import VueI18n from 'vue-i18n'
import { languages } from './lang'
import { defaultLocale } from './lang'
import axios from 'axios'
import Paginate from 'vuejs-paginate'
import VTooltip from 'v-tooltip'
import VueSession from 'vue-session'
import VueAnalytics from 'vue-analytics'
import injector from 'vue-inject'
import VueLogger from 'vuejs-logger'

import Default from './components/layouts/Default.vue'
import NoTimeline from './components/layouts/NoTimeline.vue'
import Empty from './components/layouts/Empty.vue'
import './registerServiceWorker'

import configChartHierarchy from './assets/config/dashboardChartHierarchy'

const messages = Object.assign(languages)

//Register services described in app-start.js for dependency injection
require('./app-start')

Vue.config.productionTip = process.env.NODE_ENV === 'production'
Vue.config.preproductionTip = process.env.NODE_ENV === 'test'

//Getting navigator language or language setted in user's preferences
var userLanguage = store.state.prefs.language
  ? store.state.prefs.language
  : navigator.language.split('-')[0]

//I18n
Vue.use(VueI18n)
const i18n = new VueI18n({
  locale: userLanguage ? userLanguage : defaultLocale,
  messages,
})

//Register components layouts
Vue.component('default-layout', Default)
Vue.component('no-timeline-layout', NoTimeline)
Vue.component('empty-layout', Empty)

//Session
Vue.use(VueSession)

//Dependency injection initialization
Vue.use(injector)

//Pagination
Vue.component('paginate', Paginate)

//Tooltip
Vue.use(VTooltip)

//Google analytics
Vue.use(VueAnalytics, {
  id: 'UA-131094576-1',
  router,
})

//Logger
Vue.use(VueLogger, {
  isEnabled: true,
  logLevel: Vue.config.productionTip ? 'warn' : 'debug',
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: true,
  separator: '|',
  showConsoleColors: true,
})

//Force HTTPS protocol
if (
  window.location.protocol == 'http:' &&
  (Vue.config.productionTip || Vue.config.preproductionTip)
) {
  window.location = `https://${window.location.host}${window.location.pathname}`
}

// Add default base URL
if (Vue.config.productionTip) {
  Vue.prototype.$baseURL = 'https://api.histoverydata.com'
} else if (Vue.config.preproductionTip) {
  Vue.prototype.$baseURL = 'https://api-preprod.histoverydata.com'
} else {
  Vue.prototype.$baseURL = 'http://localhost:3000'
}

//Check if user logged and his roles before loading routes
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)
  const requiresHistovery = to.matched.some(record => record.meta.requiresHistovery)

  const currentUser = store.getters['auth/user']

  // Force user to use the new role management system
  if (currentUser && !currentUser.userRole) {
    await store.dispatch('auth/removeCurrentUser')
    next('/login')
  }

  //Try to fetch root URL => redirect to his first dashboard route
  if (!currentUser && to.path == '/') {
    next('/login')
  } else if (requiresAuth && !currentUser) {
    //If there is no logged user and route need authentication
    next('/login')
  } else {
    if (
      (requiresAdmin && currentUser.userRole.name !== 'administrator') ||
      (requiresHistovery && currentUser.userRole.name === 'partner')
    ) {
      next('/dashboard/' + currentUser.tabs[0].dashboards[0].id)
    }
    if (to.name == 'Dashboard' || to.name == 'Index') {
      //Check if the currentUser can access that specific dashboard id
      const match = currentUser.tabs.find(tab => {
        var dashboard = tab.dashboards.find(d => {
          return Number(d.id) == Number(to.params.id)
        })
        return dashboard ? true : false
      })
      if (match) {
        next()
      } else {
        next('/dashboard/' + currentUser.tabs[0].dashboards[0].id)
      }
    } else if (to.name == 'Login' && currentUser) {
      if (currentUser.tabs && currentUser.tabs.length > 0) {
        var firstDashboard
        currentUser.tabs.forEach(tab => {
          tab.dashboards.forEach(dashboard => {
            firstDashboard = dashboard
            return
          })
        })
        next('/dashboard/' + firstDashboard.id)
      } else {
        next('/login')
      }
    }

    if (from.name != to.name) {
      next()
    }
  }
})

var app = {
  i18n,
  router,
  store,
  render: h => h(App),
  methods: {
    getMessage: function(key) {
      return this.$t(key)
    },
  },
  mounted: async function() {
    try {
      if (this.$route.name == 'Unsubscription') {
        return
      }

      let credentials = this.$store.getters['auth/credentials']
      let currentUser = this.$store.getters['auth/user']

      if (credentials && currentUser) {
        let response = await axios.get(
          `${this.$baseURL}/users/email/${store.state.auth.user.email}`,
        )

        //Checking available filters and filters values for current user
        response = await axios.get(`${this.$baseURL}/config/filters`)
        let filters = response.data.result

        let user = this.$store.getters['auth/user']

        //Removing filters and filters values for partners
        if (user.userRole.name == 'partner') {
          filters.filter_values.site_location = Array.from(user.sites, site => {
            return site.site
          })

          let customFilters = filters.custom
          let updatedCustomFilters = {}
          for (const key in customFilters) {
            if (filters.filter_values.site_location.indexOf(key) > -1) {
              updatedCustomFilters[key] = customFilters[key]
            }

            if (updatedCustomFilters[key]) {
              delete updatedCustomFilters[key]['site']
              delete updatedCustomFilters[key]['version']
              delete updatedCustomFilters[key]['player_version']
              delete updatedCustomFilters[key]['os_version']
              delete updatedCustomFilters[key]['os_name']
              delete updatedCustomFilters[key]['os_build']
              delete updatedCustomFilters[key]['device_model']
              delete updatedCustomFilters[key]['device_vendor']
            }
          }

          filters.filters = configChartHierarchy.partner.chart_distribution_types.visit

          delete filters.filter_values['site']
          delete filters.filter_values['version']
          delete filters.filter_values['player_version']
          delete filters.filter_values['os_version']
          delete filters.filter_values['os_name']
          delete filters.filter_values['os_build']
          delete filters.filter_values['device_model']
          delete filters.filter_values['device_vendor']
          delete filters.filter_values['state']
          delete filters.filter_values['bucket']
          delete filters.filter_values['warning_type']

          filters.custom = updatedCustomFilters
        }

        //Save new filters and filters values configuration
        await this.$store.dispatch('config/setCurrentFiltersConfig', filters)

        const chartsConfig = configChartHierarchy[user.role.toLowerCase()]
        await this.$store.dispatch('config/setCurrentChartsConfig', chartsConfig)

        localStorage.removeItem('savedRoute')
      }
      // else {
      //   //Save previous route
      //   // if (this.$router.path.indexOf('/login') == -1) {
      //   //   localStorage.setItem('savedRoute', this.$router.path)
      //   // }

      //   await this.$store.dispatch('auth/removeCurrentUser')

      //   Vue.prototype.$session.destroy()
      //   // this.$router.push('/login')
      // }
    } catch (error) {
      console.error('Error when verifying user infos:', error)
    }
  },
  components: { App },
  template: '<App/>',
}

//Access locale used globally
Object.defineProperty(Vue.prototype, '$locale', {
  get: function() {
    return app.i18n.locale
  },
  set: function(locale) {
    app.i18n.locale = locale
  },
})

Object.defineProperty(Vue.prototype, '$message', {
  get: function(key) {
    return this.$t(key)
  },
})

//Launch app component
new Vue(app).$mount('#app')
